import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import styles from './Select.module.css';

// A dropdown we lay out ourselves instead of a native <select>.
//
// Chrome draws the native option popup as an OS-level menu positioned from the
// control's layout box, and inside this app it drifts away from the control —
// most visibly for the user switcher, which lives in a position: fixed banner.
// Owning the list means its position comes from our own CSS and behaves the
// same in every browser and at every width.
const Select = ({
	value,
	onChange,
	options = [],
	label,
	variant = 'light',
	// Which edge of the trigger the popup lines up with. It is wider than the
	// trigger whenever an option is longer than the current one, so the overhang
	// has to fall towards the middle of the page: 'start' for a control on the
	// left, 'end' for one sitting against the right edge.
	align = 'start',
	className = '',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef(null);
	const buttonRef = useRef(null);

	const selected = options.find((option) => String(option.value) === String(value));

	useEffect(() => {
		if (!isOpen) return undefined;

		const handlePointerDown = (event) => {
			if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
		};
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
				buttonRef.current?.focus();
			}
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen]);

	const select = (option) => {
		onChange(option.value);
		setIsOpen(false);
		buttonRef.current?.focus();
	};

	// Up / down move through the options without opening a separate mode, which
	// is what a native select does and what screen reader users expect.
	const handleButtonKeyDown = (event) => {
		if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
		event.preventDefault();
		if (!isOpen) {
			setIsOpen(true);
			return;
		}
		const index = options.findIndex(
			(option) => String(option.value) === String(value)
		);
		const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
		if (next >= 0 && next < options.length) onChange(options[next].value);
	};

	return (
		<div
			ref={wrapperRef}
			className={`${styles.wrapper} ${styles[variant]} ${className}`}
		>
			<button
				ref={buttonRef}
				type='button'
				className={styles.trigger}
				aria-haspopup='listbox'
				aria-expanded={isOpen}
				aria-label={label}
				onClick={() => setIsOpen((open) => !open)}
				onKeyDown={handleButtonKeyDown}
			>
				<span className={styles.triggerText}>{selected?.label ?? ''}</span>
				<ChevronDown className={styles.chevron} aria-hidden='true' />
			</button>

			{isOpen && (
				<ul
					className={`${styles.list} ${
						align === 'end' ? styles.listEnd : styles.listStart
					}`}
					role='listbox'
					aria-label={label}
				>
					{options.map((option) => {
						const isSelected = String(option.value) === String(value);
						return (
							<li key={String(option.value)} className={styles.item}>
								<button
									type='button'
									role='option'
									aria-selected={isSelected}
									className={`${styles.option} ${
										isSelected ? styles.optionSelected : ''
									}`}
									onClick={() => select(option)}
								>
									<span className={styles.optionText}>{option.label}</span>
									{isSelected && (
										<Check className={styles.check} aria-hidden='true' />
									)}
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

export default Select;
